<script setup lang="ts">
import { useVModel } from '@vueuse/core';
import { computed } from 'vue';

import { AlgoliaVariant } from '@/api/algolia';
import Modal from '@/components/base/layout/Modal.vue';
import Header4 from '@/components/base/typography/Header4.vue';
import AddOnProductCard from '@/components/pdp/buy-block/add-on/AddOnProductCard.vue';
import { ProductCard as ProductCardModel, ProductCardData } from '@/utils/productCard';

const props = defineProps<{
  availableAddOns: AlgoliaVariant[];
  isOpen: boolean;
  selectedAddOns: { addOn: ProductCardData; quantity: number }[];
  title: string;
}>();

const emit = defineEmits<{
  'close-modal': [];
}>();

const formattedAddOns = computed(() => props.availableAddOns.map(ProductCardModel.fromAlgolia));

const selectedAddOns = useVModel(props, 'selectedAddOns', emit);

const quantityAdded = (sku: string) => {
  const selection = props.selectedAddOns?.find((ing) => ing.addOn.sku === sku);
  return selection?.quantity ?? 0;
};

const handleSelectAddOn = (addOn: ProductCardData, quantity: number) => {
  const addOnToUpdate = selectedAddOns.value.find((a) => a.addOn.sku === addOn.sku);

  if (addOnToUpdate) {
    if (quantity === 0) {
      selectedAddOns.value = selectedAddOns.value.filter((a) => a.addOn.sku !== addOn.sku);
    } else {
      addOnToUpdate.quantity = quantity;
    }
  } else {
    selectedAddOns.value.push({ addOn, quantity });
  }
};
</script>

<template>
  <Modal
    aria-label="Add on products to gift"
    data-test="pdp-add-on-modal"
    :isOpen="isOpen"
    :hasControls="false"
    width="md:max-w-3xl"
    @handle-close="emit('close-modal')"
  >
    <template #header>
      <Header4>{{ title }}</Header4>
    </template>
    <template #body>
      <div class="grid grid-cols-2 gap-5 mt-4 md:grid-cols-3" data-test="add-on-products-list">
        <AddOnProductCard
          v-for="(addOn, index) in formattedAddOns"
          :key="addOn.productKey"
          :addOn="addOn"
          :position="index"
          :quantity="quantityAdded(addOn.sku)"
          @select-add-on="handleSelectAddOn"
        />
      </div>
    </template>
  </Modal>
</template>
